import { useCallback } from "react";
import { personalize, personalizeUrl, type Vars } from "../../shared/personalize";
import type { TextCard } from "../../shared/types";
import { IconArrowRight, IconSparkles } from "../icons";

interface Props {
  card: TextCard;
  vars?: Vars;
  brandColor?: string;
  stepIndex?: number;
  totalSteps?: number;
  onPrimary?: () => void;
  onSecondary?: () => void;
  interactive?: boolean;
}

export default function TextSlide({
  card,
  vars = {},
  brandColor,
  stepIndex,
  totalSteps,
  onPrimary,
  onSecondary,
  interactive = true,
}: Props) {
  const t = useCallback((text: string) => personalize(text, vars), [vars]);

  const defaultBadge =
    card.role === "intro" ? "Interactive Tour" : card.role === "outro" ? "Conclusion" : "Notice";
  const badge = card.badge?.trim() ? t(card.badge) : defaultBadge;

  const defaultButton =
    card.role === "intro" ? "Start Tour" : card.role === "outro" ? "Finish" : "Next";
  const buttonLabel = card.buttonLabel?.trim() ? t(card.buttonLabel) : defaultButton;

  const handlePrimary = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!interactive) return;
    if (card.buttonUrl?.trim()) {
      window.open(personalizeUrl(card.buttonUrl.trim(), vars), "_blank", "noopener,noreferrer");
    }
    onPrimary?.();
  };

  const handleSecondary = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!interactive) return;
    if (card.secondaryUrl?.trim()) {
      window.open(personalizeUrl(card.secondaryUrl.trim(), vars), "_blank", "noopener,noreferrer");
    }
    onSecondary?.();
  };

  const styleClass = card.style === "dark" ? "ts-dark" : card.style === "accent" ? "ts-accent" : "ts-default";

  return (
    <div className={`text-slide ${styleClass} ts-${card.role}`} style={{ ["--brand" as any]: brandColor } as React.CSSProperties}>
      <div className="text-slide-content">
        <div className="text-slide-top">
          <span className="text-slide-badge">
            <IconSparkles className="icon icon-xs" />
            <span>{badge}</span>
          </span>
          {stepIndex !== undefined && totalSteps !== undefined && (
            <span className="text-slide-step">
              {card.role === "intro" ? "Start" : card.role === "outro" ? "End" : `${stepIndex + 1} / ${totalSteps}`}
            </span>
          )}
        </div>

        <h1 className="text-slide-title">{t(card.title)}</h1>

        {card.body?.trim() && (
          <div className="text-slide-body">
            {t(card.body).split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        <div className="text-slide-actions">
          <button
            type="button"
            className="btn btn-primary text-slide-btn"
            onClick={handlePrimary}
            disabled={!interactive}
          >
            <span>{buttonLabel}</span>
            <IconArrowRight />
          </button>

          {card.secondaryLabel?.trim() && (
            <button
              type="button"
              className="btn btn-ghost text-slide-btn-sec"
              onClick={handleSecondary}
              disabled={!interactive}
            >
              {t(card.secondaryLabel)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
